import { z } from 'zod';
import { ErrorResponseSchema } from '../../common/base';
import { USERNAME_MAX_LENGTH } from '../../../utils/username-validation';

/**
 * Raw username input, only length-bounded here.
 *
 * The real rules (charset, reserved words, leading letter) live in
 * `validateUsernameFormat` so one implementation serves the API, the UI and the
 * tests. This cap exists so an oversized body is rejected before it reaches
 * them, and it is generous — the format check returns the precise reason.
 */
const usernameInput = z.string().min(1, 'Enter a username').max(64);

/**
 * Request schema for checking username availability
 * POST /api/user/check-username
 */
export const CheckUsernameRequestSchema = z.object({
  /** Candidate username, normalized server-side before it is compared */
  username: usernameInput,
});

export type CheckUsernameRequest = z.infer<typeof CheckUsernameRequestSchema>;

/**
 * Why a candidate cannot be used, so the form can style the field and the
 * message consistently rather than string-matching prose.
 */
export const UsernameUnavailableReasonSchema = z.enum([
  'taken',
  'reserved',
  'invalid',
]);

export type UsernameUnavailableReason = z.infer<typeof UsernameUnavailableReasonSchema>;

const CheckUsernameSuccessSchema = z.object({
  success: z.literal(true),
  /** The normalized form the name would actually be stored as */
  username: z.string().max(USERNAME_MAX_LENGTH),
  /** Whether the candidate can be claimed by the requesting user */
  available: z.boolean(),
  /** Present only when `available` is false */
  reason: UsernameUnavailableReasonSchema.optional(),
  /** Sentence to show under the field. Present only when unavailable. */
  error: z.string().optional(),
});

export type CheckUsernameSuccess = z.infer<typeof CheckUsernameSuccessSchema>;

export const CheckUsernameResponseSchema = z.discriminatedUnion('success', [
  CheckUsernameSuccessSchema,
  ErrorResponseSchema,
]);

export type CheckUsernameResponse = z.infer<typeof CheckUsernameResponseSchema>;

/**
 * Request schema for committing a username change
 * POST /api/user/change-username
 */
export const ChangeUsernameRequestSchema = z.object({
  /** The new username to claim */
  username: usernameInput,
  /**
   * The seller must retype the name to confirm.
   *
   * The change is one-way and re-points a public URL, so it gets the same
   * treatment as a destructive action rather than a silent field save. Compared
   * after normalization, so casing and stray whitespace do not fail it.
   */
  confirmUsername: usernameInput,
});

export type ChangeUsernameRequest = z.infer<typeof ChangeUsernameRequestSchema>;

const ChangeUsernameSuccessSchema = z.object({
  success: z.literal(true),
  /** The username now stored */
  username: z.string().max(USERNAME_MAX_LENGTH),
  /** The name it moved off, which now 301s to the new one */
  previousUsername: z.string().nullable(),
  /** New public store URL, so the UI can show it without rebuilding the path */
  storeUrl: z.string(),
  /** How many listings were re-indexed and revalidated onto the new URL */
  listingsReindexed: z.number().int().nonnegative(),
});

export type ChangeUsernameSuccess = z.infer<typeof ChangeUsernameSuccessSchema>;

export const ChangeUsernameResponseSchema = z.discriminatedUnion('success', [
  ChangeUsernameSuccessSchema,
  ErrorResponseSchema,
]);

export type ChangeUsernameResponse = z.infer<typeof ChangeUsernameResponseSchema>;

/**
 * Response schema for the change-eligibility probe
 * GET /api/user/change-username
 *
 * The form needs to know before it renders whether this account still has a
 * change available, so it can show the field as read-only with an explanation
 * instead of letting someone type a name and then rejecting it.
 */
const UsernameStatusSuccessSchema = z.object({
  success: z.literal(true),
  /** Current username */
  username: z.string(),
  /** Whether a change can be made right now */
  canChange: z.boolean(),
  /** Why not, when `canChange` is false */
  error: z.string().optional(),
});

export const UsernameStatusResponseSchema = z.discriminatedUnion('success', [
  UsernameStatusSuccessSchema,
  ErrorResponseSchema,
]);

export type UsernameStatusResponse = z.infer<typeof UsernameStatusResponseSchema>;
